/**
 * @quotech/auth - SSO Handler Component
 * 
 * Handles SSO token from URL parameters.
 * Place at the top of your App or in a dedicated /sso route.
 * 
 * URL format: https://module.quotech.ai/sso?token=xxx&org_id=yyy&returnTo=/path
 */

import { useEffect, useState, type ReactNode } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useAuthStore } from './authStore'
import { MASTER_LOGIN_URL } from './cookieAuth'
import { tokenMatchesUser, type CachedIdentityLike } from './identityReconcile'
import type { SSOParams } from './types'

/**
 * Decide whether an incoming SSO token can be skipped (just redirect) or
 * MUST be processed via setFromSSO. Pure + exported for tests.
 *
 * We only skip when we are CERTAIN the incoming token is for the SAME user
 * we already hold (verdict === 'match'). For a different account
 * ('mismatch') OR when we cannot prove it's the same user
 * ('indeterminate' — undecodable token / no current user), we PROCESS the
 * token so the account SWITCH actually happens. This restores the
 * documented intent that previously sat as dead code below an
 * unconditional `if (isAuthenticated) return` — the SSO half of
 * BUG-2026-06-02-COLD-LOAD-IDENTITY-ADOPTION (operator landed on
 * dev@quotech.ai despite signing in as another account from a hub link).
 */
// eslint-disable-next-line react-refresh/only-export-components -- pure, side-effect-free decision helper colocated with its only consumer; extracted solely for unit testing.
export function shouldSkipSsoProcessing(input: {
  isAuthenticated: boolean
  token: string | null | undefined
  currentUser: CachedIdentityLike | null | undefined
}): boolean {
  if (!input.isAuthenticated) return false
  return tokenMatchesUser(input.token, input.currentUser) === 'match'
}

export interface SSOHandlerProps {
  /** Called when SSO succeeds */
  onSuccess?: () => void
  /** Called when SSO fails */
  onError?: (error: string) => void
  /** Where to redirect on success (default: /) */
  defaultRedirect?: string
  /** Custom loading component */
  loadingComponent?: ReactNode
  /** Custom error component */
  errorComponent?: (error: string) => ReactNode
  /** Whether to remove token from URL after processing */
  cleanUrl?: boolean
}

/**
 * Extract SSO params from URL
 */
function extractSSOParams(searchParams: URLSearchParams): SSOParams {
  return {
    token: searchParams.get('token'),
    refreshToken: searchParams.get('refreshToken') || searchParams.get('refresh_token'),
    orgId: searchParams.get('org_id') || searchParams.get('orgId'),
    orgName: searchParams.get('org_name') || searchParams.get('orgName'),
    // Support multiple param names: returnTo, return_to, to (used by CRMRedirect)
    returnTo: searchParams.get('returnTo') || searchParams.get('return_to') || searchParams.get('to'),
  }
}

/**
 * Default loading component
 */
function DefaultLoading() {
  return (
    <div className="flex items-center justify-center min-h-screen bg-slate-50 dark:bg-slate-900">
      <div className="flex flex-col items-center gap-4">
        <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-slate-500 dark:text-slate-400">Signing you in...</p>
      </div>
    </div>
  )
}

/**
 * Default error component
 */
function DefaultError({ error }: { error: string }) {
  return (
    <div className="flex items-center justify-center min-h-screen bg-slate-50 dark:bg-slate-900">
      <div className="flex flex-col items-center gap-4 p-6 bg-white dark:bg-slate-800 rounded-lg shadow-lg max-w-md">
        <div className="w-12 h-12 flex items-center justify-center bg-red-100 dark:bg-red-900/30 rounded-full">
          <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Authentication Failed</h2>
        <p className="text-sm text-slate-500 dark:text-slate-400 text-center">{error}</p>
        <a
          href={MASTER_LOGIN_URL}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Return to Dashboard
        </a>
      </div>
    </div>
  )
}

export function SSOHandler({
  onSuccess,
  onError,
  defaultRedirect = '/',
  loadingComponent,
  errorComponent,
  cleanUrl = true,
}: SSOHandlerProps) {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const { setFromSSO, isAuthenticated } = useAuthStore()
  const [error, setError] = useState<string | null>(null)
  const [processing, setProcessing] = useState(false)

  useEffect(() => {
    const params = extractSSOParams(searchParams)

    // No SSO token in URL - nothing to process
    if (!params.token) {
      // However, if already authenticated and there's a returnTo, navigate there
      // This handles the case where user clicks a hub link while already logged in
      if (isAuthenticated && params.returnTo) {
        navigate(params.returnTo, { replace: true })
      }
      return
    }

    // Already processing - don't start again
    if (processing) {
      return
    }

    // If already authenticated AS THE SAME USER the incoming token is for,
    // skip the redundant re-auth and just redirect. For a different account
    // (or when we can't prove it's the same user) we deliberately fall
    // through to setFromSSO below so the account switch actually happens.
    if (shouldSkipSsoProcessing({ token: params.token, isAuthenticated, currentUser: useAuthStore.getState().user })) {
      const redirectPath = params.returnTo || defaultRedirect
      
      // Clean URL if requested
      if (cleanUrl) {
        const newParams = new URLSearchParams(searchParams)
        newParams.delete('token')
        newParams.delete('refreshToken')
        newParams.delete('refresh_token')
        newParams.delete('org_id')
        newParams.delete('orgId')
        newParams.delete('org_name')
        newParams.delete('orgName')
        newParams.delete('returnTo')
        newParams.delete('return_to')
        setSearchParams(newParams, { replace: true })
      }
      
      navigate(redirectPath, { replace: true })
      return
    }

    // NOTE: We process SSO token even if we think we're authenticated
    // This handles cases where:
    // 1. User has stale/expired auth but receives fresh SSO token
    // 2. User switches accounts via SSO
    // 3. User is re-authenticating after logout from different hub

    setProcessing(true)

    const handleSSO = async () => {
      try {
        const success = await setFromSSO(params.token!, params.orgId, params.orgName)

        if (success) {
          // Clean URL if requested
          if (cleanUrl) {
            const newParams = new URLSearchParams(searchParams)
            newParams.delete('token')
            newParams.delete('refreshToken')
            newParams.delete('refresh_token')
            newParams.delete('org_id')
            newParams.delete('orgId')
            newParams.delete('org_name')
            newParams.delete('orgName')
            setSearchParams(newParams, { replace: true })
          }

          onSuccess?.()

          // Navigate to return path
          const redirectPath = params.returnTo || defaultRedirect
          navigate(redirectPath, { replace: true })
        } else {
          const errMsg = 'Failed to authenticate with provided token'
          setError(errMsg)
          onError?.(errMsg)
        }
      } catch (err) {
        const errMsg = err instanceof Error ? err.message : 'SSO authentication failed'
        setError(errMsg)
        onError?.(errMsg)
      } finally {
        setProcessing(false)
      }
    }

    handleSSO()
  }, [searchParams, setSearchParams, navigate, setFromSSO, processing, cleanUrl, defaultRedirect, onSuccess, onError, isAuthenticated])

  // Show error state (even if token is still in URL)
  if (error) {
    return errorComponent ? <>{errorComponent(error)}</> : <DefaultError error={error} />
  }

  // Show loading while actively processing SSO token
  // Only show if processing is true (not just token in URL, which could be stale)
  if (processing) {
    return <>{loadingComponent || <DefaultLoading />}</>
  }

  // No SSO token or finished processing - render nothing (other routes will handle)
  return null
}

export default SSOHandler
